import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router';
import { init } from '@module-federation/runtime';
import { useEffect } from 'react';

import type { REMOTE_MODULES } from 'routes/remoteModules';

import { RemoteModuleLoader } from './RemoteModuleLoader';

import { Page } from 'components';
import {
  APP_ROOT,
  AUTHORISATIONS,
  AUTHORISATIONS_OVA,
  COURSE_REGISTRATION,
  EDUCATION_PREPARATION,
  STUDENTS,
  UNAUTHORISED,
} from 'routes';
import { INIT_MODULES } from 'env';
import { useProfile } from 'providers/UserProfileProvider';
import { ApplicationModule } from 'types/__generated__';

init(INIT_MODULES);

const ROUTES = [
  {
    path: EDUCATION_PREPARATION,
    remote: 'facet',
    applicationModule: ApplicationModule.Facet,
  },
  {
    path: COURSE_REGISTRATION,
    remote: 'prism',
    applicationModule: ApplicationModule.Prism,
  },
  {
    path: STUDENTS,
    remote: 'quartz',
    applicationModule: ApplicationModule.Quartz,
  },
  {
    path: AUTHORISATIONS,
    remote: 'gobletLegacy',
    applicationModule: ApplicationModule.GobletLegacy,
  },
  {
    path: AUTHORISATIONS_OVA,
    remote: 'goblet',
    applicationModule: ApplicationModule.Goblet,
  },
];

export function MainLayoutPage() {
  const profile = useProfile();
  const location = useLocation();
  const navigate = useNavigate();

  const defaultRoute = profile.enabledModules.includes(ApplicationModule.Facet)
    ? EDUCATION_PREPARATION
    : COURSE_REGISTRATION;

  const getApplicationModule = (routePath: string): ApplicationModule | boolean => {
    const route = ROUTES.find((route) => routePath.includes(route.path));
    if (!route) {
      return false;
    }
    return route.applicationModule;
  };

  useEffect(() => {
    if (location.pathname === APP_ROOT) return;

    const requestedModule = getApplicationModule(location.pathname);
    if (!requestedModule) return;

    const isAuthorised = profile.enabledModules.includes(requestedModule as ApplicationModule);
    if (!isAuthorised) {
      navigate(UNAUTHORISED);
    }
  }, [location.pathname, navigate, profile.enabledModules]);

  return (
    <Page>
      <Routes>
        <Route index path={`${APP_ROOT}`} element={<Navigate to={`${defaultRoute}`} replace />} />
        {ROUTES.map(
          ({ path, remote, applicationModule }) =>
            profile.enabledModules.includes(applicationModule) && (
              <Route
                key={path}
                path={`${path}*`}
                element={<RemoteModuleLoader remote={`${remote}` as keyof typeof REMOTE_MODULES} />}
              />
            )
        )}
      </Routes>
      <Outlet />
    </Page>
  );
}
